import { useEffect, type RefObject } from "react";
import { useLockStore } from "../../store/lockStore";
import { lockApi } from "../../api/lockApi";
import { asError, eventBus } from "@optimyl/events";

const POLL_INTERVAL_MS = 15000;

/**
 * Auto-discovery for the case where a holder vanishes silently (network
 * crash, sleep, tab killed without `beforeunload` firing). No SignalR
 * event fires when the lock's TTL expires server-side, so a viewer
 * sitting on the lock screen has no way to learn the lock is gone.
 *
 * This sub-hook polls `acquireLock` every 15 seconds while the user is
 * on the lock screen but NOT actively requesting (the requesting state
 * has its own recovery via `useClaimStuckSelfHeal` at countdown=0).
 * Three outcomes per poll:
 *
 *  - acquired=true: the lock TTL'd and we just took it. Run the same
 *    refetch-then-flip dance as `onLockReleased` so a submitted form
 *    routes to StepThankYou cleanly.
 *  - acquired=false with different lockedBy: someone else took the lock
 *    without us getting a push. Update the displayed holder.
 *  - acquired=false with same lockedBy: no change. Keep polling.
 *
 * Cadence: 15s is 5× the holder's heartbeat, keeping the request load
 * modest while still surfacing TTL expiry within a reasonable window.
 * Worst-case detection time = TTL (90s) + poll interval (15s) = ~105s.
 */
export function useLockScreenPoll(
  enabled: boolean,
  formId: string | null,
  sessionId: string,
  lockStatus: string,
  formIdRef: RefObject<string | null>,
  sessionIdRef: RefObject<string>,
  onLockGrantedRef: RefObject<(() => Promise<void>) | undefined>,
): void {
  const setLockStatus = useLockStore((s) => s.setLockStatus);

  useEffect(() => {
    if (!enabled || !formId) return;
    if (lockStatus !== "locked-by-other") return;

    const id = window.setInterval(async () => {
      if (!formIdRef.current) return;
      try {
        const result = await lockApi.acquireLock(formIdRef.current, sessionIdRef.current);
        if (result.acquired) {
          // The lock was gone — we just took it via Path 1 (fresh acquire).
          // Refetch form data before flipping so a submitted form routes to
          // StepThankYou cleanly, same as the onLockReleased recovery path.
          const reloadPromise = onLockGrantedRef.current
            ? onLockGrantedRef.current().catch((err) => {
                eventBus.publish("error", { error: asError(err), context: "useLockScreenPoll.reloadAfterAcquire" });
              })
            : Promise.resolve();
          await reloadPromise;
          setLockStatus("locked");
          return;
        }
        // Still held by someone. If the holder's identity changed without a
        // push reaching us, update the displayed holder. Comparing by string;
        // the lockStore allows null so check both sides.
        const current = useLockStore.getState().lockedBy;
        if (result.lockedBy && result.lockedBy !== current) {
          setLockStatus("locked-by-other", result.lockedBy);
        }
      } catch (err) {
        eventBus.publish("error", { error: asError(err), context: "useLockScreenPoll" });
      }
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(id);
  }, [enabled, formId, sessionId, lockStatus, formIdRef, sessionIdRef, onLockGrantedRef, setLockStatus]);
}
